// Accruals: costs or income recorded before the bill arrives, spread across
// the months they belong to. Each period is recorded to the books in turn
// (the release step debits the expense account and credits the accrual
// account); a recorded period can be undone with an exact mirror entry.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  type AccrualSchedule,
  createAccrualSchedule,
  listAccrualSchedules,
} from "@/lib/api";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "cancelled":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function recordedMinor(schedule: AccrualSchedule): number {
  return schedule.entries
    .filter((entry) => entry.posted)
    .reduce((sum, entry) => sum + entry.amount_minor, 0);
}

function remainingMinor(schedule: AccrualSchedule): number {
  return schedule.total_minor - recordedMinor(schedule);
}

// The create form has more fields than the shared document-draft mirror
// carries, so the whole draft lives in this page's state. The drawer reads
// and writes it through `draft`/`updateDraft`, which is what lets a draft
// survive closing and reopening the panel.
interface AccrualDraft {
  description: string;
  total: string;
  periods: string;
  startDate: string;
  endDate: string;
  accountId: string;
  reverseAccountId: string;
}

function newAccrualDraft(): AccrualDraft {
  return {
    description: "",
    total: "",
    periods: "1",
    startDate: todayInput(),
    endDate: todayInput(),
    accountId: "",
    reverseAccountId: "",
  };
}

export function AccrualsPage() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<AccrualSchedule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<AccrualDraft>(newAccrualDraft);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const reload = useCallback(() => {
    setError(null);
    listAccrualSchedules()
      .then(setSchedules)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, []);

  useEffect(reload, [reload]);

  const updateDraft = (patch: Partial<AccrualDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const clearDraft = () => {
    setDraft(newAccrualDraft());
    setFormError(null);
  };

  const hasDraft =
    draft.description.trim() !== "" ||
    draft.total.trim() !== "" ||
    draft.accountId !== "" ||
    draft.reverseAccountId !== "";

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      if (!draft.description.trim())
        throw new Error("Description is required.");
      const value = Number(draft.total.trim());
      if (!Number.isFinite(value) || value <= 0)
        throw new Error("Total must be a positive amount.");
      const total_minor = Math.round(value * 100);
      const count = Number(draft.periods);
      if (!Number.isInteger(count) || count <= 0)
        throw new Error("Periods must be a whole number.");
      if (!draft.startDate || !draft.endDate)
        throw new Error("Start and end dates are required.");
      if (draft.endDate < draft.startDate)
        throw new Error("The end date must be on or after the start date.");
      if (!draft.accountId || !draft.reverseAccountId)
        throw new Error("Pick the expense account and the accrual account.");
      const created = await createAccrualSchedule({
        description: draft.description.trim(),
        total_minor,
        periods: count,
        start_date: draft.startDate,
        end_date: draft.endDate,
        account_id: draft.accountId,
        reverse_account_id: draft.reverseAccountId,
      });
      clearDraft();
      setCreating(false);
      reload();
      void navigate({
        to: "/accruals/$accrualScheduleId",
        params: { accrualScheduleId: created.id },
      });
    } catch (cause) {
      setFormError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const statusCounts = (schedules ?? []).reduce<Record<string, number>>(
    (counts, schedule) => {
      const key = schedule.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  // Client-side filter over the fetched list.
  const visibleSchedules = (schedules ?? [])
    .filter(
      (schedule) => !statusTab || schedule.status.toLowerCase() === statusTab,
    )
    .filter((schedule) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return schedule.description.toLowerCase().includes(query);
    });

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Accruals"
        description="Costs recorded before the bill arrives — each month's share is moved to the books in turn."
        actions={
          <Button type="button" size="sm" onClick={() => setCreating(true)}>
            New accrual
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <Tabs
        label="Filter accruals by status"
        tabs={[
          { value: "", label: `All (${(schedules ?? []).length})` },
          ...statusTabs.map((status) => ({
            value: status,
            label: `${plainStatus(status)} (${statusCounts[status]})`,
          })),
        ]}
        value={statusTab}
        onValueChange={setStatusTab}
      >
        <div className="surface grid gap-3 rounded-2xl p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label
              htmlFor="accruals-search"
              className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
            >
              Search
              <Input
                id="accruals-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="What it's for…"
              />
            </label>
            <Button type="button" variant="secondary" onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>

        <div
          className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
          data-testid="accruals-table"
        >
          <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>What it&apos;s for</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Recorded</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules === null ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {error ? "Could not load accruals." : "Loading…"}
                    </TableCell>
                  </TableRow>
                ) : schedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {error
                        ? "Could not load accruals."
                        : "No accruals yet — create the first one above."}
                    </TableCell>
                  </TableRow>
                ) : visibleSchedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No accruals match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleSchedules.map((schedule) => (
                    <TableRow key={schedule.id}>
                      <TableCell>
                        <Link
                          to="/accruals/$accrualScheduleId"
                          params={{ accrualScheduleId: schedule.id }}
                          className="font-medium hover:underline"
                        >
                          {schedule.description}
                        </Link>
                        <span className="block text-xs text-[var(--muted-foreground)]">
                          {schedule.periods}{" "}
                          {schedule.periods === 1 ? "period" : "periods"}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs">
                        {new Date(schedule.start_date).toLocaleDateString()} →{" "}
                        {new Date(schedule.end_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={statusTone(schedule.status)}
                          label={plainStatus(schedule.status)}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: recordedMinor(schedule),
                          currency: "USD",
                        })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: remainingMinor(schedule),
                          currency: "USD",
                        })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: schedule.total_minor,
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div
          className="stack-list grid gap-3 min-[64rem]:hidden"
          data-testid="accruals-stack"
        >
          {schedules === null ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {error ? "Could not load accruals." : "Loading…"}
            </p>
          ) : schedules.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {error
                ? "Could not load accruals."
                : "No accruals yet — create the first one."}
            </p>
          ) : visibleSchedules.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              No accruals match these filters.
            </p>
          ) : (
            visibleSchedules.map((schedule) => (
              <article
                key={schedule.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <Link
                    to="/accruals/$accrualScheduleId"
                    params={{ accrualScheduleId: schedule.id }}
                    className="record-title underline-offset-4 hover:underline"
                  >
                    {schedule.description}
                  </Link>
                  <span className="tabular-nums">
                    {formatMoney({
                      cents: schedule.total_minor,
                      currency: "USD",
                    })}
                  </span>
                </div>
                <CardField label="Period">
                  {new Date(schedule.start_date).toLocaleDateString()} →{" "}
                  {new Date(schedule.end_date).toLocaleDateString()}
                </CardField>
                <CardField label="Status">
                  <StatusDot
                    tone={statusTone(schedule.status)}
                    label={plainStatus(schedule.status)}
                  />
                </CardField>
                <CardField label="Recorded">
                  {formatMoney({
                    cents: recordedMinor(schedule),
                    currency: "USD",
                  })}
                </CardField>
                <CardField label="Remaining">
                  {formatMoney({
                    cents: remainingMinor(schedule),
                    currency: "USD",
                  })}
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/accruals/$accrualScheduleId"
                      params={{ accrualScheduleId: schedule.id }}
                    >
                      Open
                    </Link>
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>
      </Tabs>

      <Drawer
        open={creating}
        onOpenChange={(next) => {
          if (!next) setCreating(false);
        }}
        title="New accrual"
        description="Spreads the total across the periods. Recording a period posts it. This does not create a supplier bill. Closing this panel keeps your draft on this page."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreating(false)}
              disabled={submitting}
            >
              Back to accruals
            </Button>
            {hasDraft ? (
              <Button
                type="button"
                variant="ghost"
                onClick={clearDraft}
                disabled={submitting}
              >
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="accrual-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Creating…" : "Create accrual"}
            </Button>
          </>
        }
      >
        <form
          id="accrual-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create accrual"
        >
          {formError ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {formError}
            </p>
          ) : null}
          <label
            htmlFor="accrual-description"
            className="grid gap-1 text-xs font-medium"
          >
            What it is for
            <Input
              id="accrual-description"
              value={draft.description}
              onChange={(event) =>
                updateDraft({ description: event.target.value })
              }
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-total"
            className="grid gap-1 text-xs font-medium"
          >
            Total (USD)
            <Input
              id="accrual-total"
              type="number"
              min="0"
              step="0.01"
              value={draft.total}
              onChange={(event) => updateDraft({ total: event.target.value })}
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-periods"
            className="grid gap-1 text-xs font-medium"
          >
            Periods
            <Input
              id="accrual-periods"
              type="number"
              min="1"
              step="1"
              value={draft.periods}
              onChange={(event) => updateDraft({ periods: event.target.value })}
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-start"
            className="grid gap-1 text-xs font-medium"
          >
            Starts
            <Input
              id="accrual-start"
              type="date"
              value={draft.startDate}
              onChange={(event) =>
                updateDraft({ startDate: event.target.value })
              }
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-end"
            className="grid gap-1 text-xs font-medium"
          >
            Ends
            <Input
              id="accrual-end"
              type="date"
              value={draft.endDate}
              onChange={(event) => updateDraft({ endDate: event.target.value })}
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-account"
            className="grid gap-1 text-xs font-medium"
          >
            Expense account
            <AccountPicker
              id="accrual-account"
              value={draft.accountId}
              onChange={(accountId) => updateDraft({ accountId })}
              required
              disabled={submitting}
            />
          </label>
          <label
            htmlFor="accrual-reverse-account"
            className="grid gap-1 text-xs font-medium"
          >
            Accrual account
            <AccountPicker
              id="accrual-reverse-account"
              value={draft.reverseAccountId}
              onChange={(reverseAccountId) =>
                updateDraft({ reverseAccountId })
              }
              required
              disabled={submitting}
            />
          </label>
        </form>
      </Drawer>
    </div>
  );
}
